import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const mProvider = params.get("provider");
  const mTopupId = params.get("topup_id");
  const mPaymentId = params.get("id");
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [amount, setAmount] = useState(0);
  const polls = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    // PayPal (sandbox) return: capture server-side once.
    if (mProvider === "paypal" && mTopupId) {
      api.post(`/payments/paypal/capture?topup_id=${encodeURIComponent(mTopupId)}`)
        .then((r) => { if (r.data.credited) { setAmount(r.data.amount); setState("paid"); } else setState("failed"); })
        .catch(() => setState("failed"));
      return;
    }
    // Moyasar return: verify the payment server-side once.
    if (mProvider === "moyasar" && mTopupId && mPaymentId) {
      const verify = async () => {
        try {
          const res = await api.post(
            `/payments/moyasar/verify?payment_id=${encodeURIComponent(mPaymentId)}&topup_id=${encodeURIComponent(mTopupId)}`
          );
          if (res.data.credited || res.data.status === "paid") {
            const s = await api.get(`/payments/moyasar/status/${mTopupId}`);
            setAmount(s.data.amount);
            setState("paid");
          } else {
            setState("failed");
          }
        } catch {
          setState("failed");
        }
      };
      verify();
      return;
    }
    if (!sessionId) { setState("failed"); return; }
    const poll = async () => {
      try {
        const endpoint = sessionId.startsWith("fs_")
          ? `/payments/fastspring/status/${sessionId}`
          : `/payments/status/${sessionId}`;
        const res = await api.get(endpoint);
        if (res.data.payment_status === "paid") {
          setAmount(res.data.amount);
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (err) {
        if (err.response?.status === 404) { setState("failed"); return; }
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("failed"); return; }
      timer.current = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [sessionId, mProvider, mTopupId, mPaymentId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-sm w-full text-center"
      >
        {state === "polling" && (
          <>
            <Loader2 className="w-14 h-14 animate-spin text-primary mx-auto" />
            <p className="mt-5 font-semibold">{t.processingPayment}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-primary/15 grid place-items-center mx-auto animate-pop-in">
              <CheckCircle2 className="w-11 h-11 text-primary" />
            </div>
            <h1 className="text-2xl font-black mt-5">{t.paymentSuccess}</h1>
            <p className="text-muted-foreground mt-2">{t.paymentSuccessDesc}</p>
            <p className="text-3xl font-black text-primary tabular-nums mt-4">
              +{formatSAR(amount, lang)} <span className="text-base">{t.currency}</span>
            </p>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/15 grid place-items-center mx-auto animate-pop-in">
              <XCircle className="w-11 h-11 text-destructive" />
            </div>
            <h1 className="text-2xl font-black mt-5">{t.paymentCancelled}</h1>
            <p className="text-muted-foreground mt-2">{t.paymentCancelledDesc}</p>
          </>
        )}
        {state !== "polling" && (
          <Button
            data-testid="payment-back-to-wallet"
            onClick={() => navigate("/", { replace: true })}
            className="w-full h-[52px] rounded-2xl text-base font-bold mt-8"
          >
            {t.backToWallet}
          </Button>
        )}
      </motion.div>
    </div>
  );
}
